import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useAdminGetBroadcastNotifications } from '@/api/auth/admin/admin';

interface BroadcastNotificationsCardProps {
  broadcastId: string;
}

const PAGE_SIZE = 10;

/**
 * Card component to display notifications for a broadcast
 */
export function BroadcastNotificationsCard({ broadcastId }: BroadcastNotificationsCardProps) {
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useAdminGetBroadcastNotifications(broadcastId, {
    page,
    pageSize: PAGE_SIZE,
  });

  const notifications = data?.data?.notifications ?? [];
  const totalCount = data?.data?.totalCount ?? 0;
  const succeededCount = data?.data?.succeededCount ?? 0;
  const failedCount = data?.data?.failedCount ?? 0;
  const queuedCount = data?.data?.queuedCount ?? 0;

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const getTypeBadge = (type?: string) => {
    switch (type) {
      case 'user':
        return <Badge variant="outline">User</Badge>;
      case 'org':
        return <Badge variant="outline">Org</Badge>;
      case 'email':
        return <Badge variant="outline">Email</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'Succeeded':
        return <Badge className="bg-green-500">Succeeded</Badge>;
      case 'Failed':
        return <Badge variant="destructive">Failed</Badge>;
      case 'Queued':
        return <Badge variant="secondary">Queued</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  if (totalCount === 0 && !isLoading) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notifications</CardTitle>
        <CardDescription>Individual notification delivery status</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4 mb-4">
          <Badge variant="secondary">{totalCount} total</Badge>
          {succeededCount > 0 && <Badge className="bg-green-500">{succeededCount} succeeded</Badge>}
          {failedCount > 0 && <Badge variant="destructive">{failedCount} failed</Badge>}
          {queuedCount > 0 && <Badge variant="outline">{queuedCount} queued</Badge>}
        </div>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : error ? (
          <div className="text-center text-destructive py-4">Failed to load notifications</div>
        ) : (
          <>
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Target ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Attempts</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {notifications.map((notification) => (
                    <TableRow key={notification.id}>
                      <TableCell>{getTypeBadge(notification.type)}</TableCell>
                      <TableCell className="text-sm font-mono">
                        {notification.targetId || '-'}
                      </TableCell>
                      <TableCell>{getStatusBadge(notification.status)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(notification.createdUtc)}
                      </TableCell>
                      <TableCell>{notification.attempts}</TableCell>
                      <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                        {notification.errorMessage || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
